import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import Countdown, { zeroPad } from 'react-countdown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import actions from '../../../redux/actions';
import { RootState } from '../../../redux/store';
import { CycleStatus } from '../../../types/CycleStatus';
import { TesterStatusInCycle } from '../../../types/TesterStatusInCycle';
import { WorkCategory } from '../../../types/WorkCategory';
import useIndicators from '../../../util/useIndicators';
import { colors, layout } from '../../../theme/tokens';

/* Header + tabs of every tester cycle page (replaces the old Cycle/Sidemenu sidebar).
   Same rules as before: which tabs show depends on the work category and the tester's status;
   "I have finished" is enabled once bugs are reported and the test plan (if any) is complete. */

const WORKING_STATUSES = [TesterStatusInCycle.Approved, TesterStatusInCycle.AwaitingReview, TesterStatusInCycle.Finished];

type CycleTab = { label: string; to: string; exact?: boolean; count?: React.ReactNode };

function isFinishEnabled(cycle): boolean {
    if (!cycle || cycle.status === CycleStatus.Completed) {
        return false;
    }

    switch (cycle.workCategory) {
        case WorkCategory.FeedbackAndRating:
            return true;
        case WorkCategory.FindBugsExploratory:
            return !!cycle.bugsFound;
        case WorkCategory.FindBugsTestPlan:
            return !!cycle.bugsFound && cycle.testPlan.length === cycle.testPlanResults.length;
        default:
            console.error(`Unknown work category for cycleId ${cycle.id}:`, cycle.workCategory);
            return false;
    }
}

const CountChip = ({ value, color = 'default' as 'default' | 'error' | 'success', title = '' }) => (
    <Chip size="small" label={value} color={color} title={title} sx={{ ml: 0.75, height: 20, fontSize: 12 }} />
);

interface Props {
    cycleId: number;
}

const TesterCycleNav: React.FC<Props> = ({ cycleId }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { cycles, chatMessages } = useSelector((state: RootState) => state);
    const { indicators } = useIndicators(true);

    const cycle: any = (cycles as any[]).find((el) => el.id === cycleId);

    // Cycle messages from the owner, refreshed every 10 s (unread count on the Messages tab)
    const ownerId = cycle?.ownerId;
    useEffect(() => {
        if (!ownerId) {
            return;
        }
        // Load now (the Messages page relies on it), then keep polling
        dispatch(actions.listChatMessages(cycleId, ownerId));
        const interval = setInterval(() => dispatch(actions.listChatMessages(cycleId, ownerId)), 10000);
        return () => clearInterval(interval);
    }, [cycleId, ownerId]);

    // Once the tester finishes, the cycle moves to awaiting review: back to the dashboard
    const previousStatus = useRef(cycle?.testerStatus);
    useEffect(() => {
        if (previousStatus.current && previousStatus.current !== cycle?.testerStatus && cycle?.testerStatus === TesterStatusInCycle.AwaitingReview) {
            history.push('/account');
        }
        previousStatus.current = cycle?.testerStatus;
    }, [cycle?.testerStatus]);

    if (!cycle) {
        return null;
    }

    const working = WORKING_STATUSES.includes(cycle.testerStatus);
    const base = `/account/tester/cycle/${cycle.id}`;

    const ownerMessages = (chatMessages.byCycleId[cycle.id] || {})[cycle.ownerId] || [];
    const unreadMessages = ownerMessages.filter((message) => !message.read).length;
    const unreadComments = indicators.filter((indicator) => indicator.entityType === 'chat' && indicator.cycleId === cycle.id).length;

    const tabs: CycleTab[] = [{ label: 'Cycle details', to: base, exact: true }];

    // Feedback cycles get no extra tab: their questions are listed on Cycle details.
    if (cycle.workCategory !== WorkCategory.FeedbackAndRating) {
        if (cycle.workCategory === WorkCategory.FindBugsTestPlan) {
            const passed = cycle.testPlanResults?.length || 0;
            const total = cycle.testPlan?.length || 0;
            tabs.push({
                label: 'Test plan',
                to   : `${base}/testplan`,
                count: working ? <CountChip value={`${passed} / ${total}`} color={passed === total ? 'success' : 'default'} /> : null,
            });
        }
        if (working) {
            tabs.push({ label: 'New bug', to: `${base}/newbug` });
            tabs.push({
                label: 'Reported bugs',
                to   : `${base}/bugs`,
                count: (
                    <>
                        <CountChip value={cycle.bugsFound || 0} />
                        {unreadComments > 0 && (
                            <CountChip value={unreadComments} color="error" title={`${unreadComments} unread bug comment${unreadComments === 1 ? '' : 's'}`} />
                        )}
                    </>
                ),
            });
        }
    }

    if (working) {
        tabs.push({
            label: 'Messages',
            to   : `${base}/messages`,
            count: unreadMessages > 0 ? <CountChip value={unreadMessages} color="error" title={`${unreadMessages} unread message${unreadMessages === 1 ? '' : 's'}`} /> : null,
        });
    }

    // The deepest tab whose path the URL is under (e.g. /bugs/12/edit stays on "Reported bugs")
    const activeIndex = tabs.reduce((best, tab, index) => {
        const matches = tab.exact ? pathname === tab.to : pathname.startsWith(tab.to);
        return matches && (best === -1 || tab.to.length > tabs[best].to.length) ? index : best;
    }, -1);

    const showCountdown = cycle.workCategory !== WorkCategory.FeedbackAndRating && moment().isBefore(moment(cycle.endDate));
    const finishEnabled = isFinishEnabled(cycle);
    const completed = cycle.testerStatus === TesterStatusInCycle.AwaitingReview || cycle.testerStatus === TesterStatusInCycle.Finished;

    return (
        <Box sx={{ px: 3, pt: 2, borderBottom: `1px solid ${colors.dividerBlue}`, backgroundColor: '#fff' }}>
            {/* Same width as the capped page content below (PageContainer), so tabs line up with the cards */}
            <Box sx={{ maxWidth: typeof layout.contentMaxWidth === 'number' ? layout.contentMaxWidth - 48 : 'none', mx: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                    <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => history.push('/account/tester')}>
                        Test cycles
                    </Button>

                    <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600, color: colors.titleBlue, flex: 1, minWidth: 200 }}>
                        {cycle.title}
                    </Typography>

                    {showCountdown && (
                        <Typography sx={{ fontSize: 13, color: colors.textGrey }}>
                            Cycle ends in{' '}
                            <Countdown
                                date={cycle.endDate}
                                daysInHours={false}
                                renderer={({ days, hours, minutes, seconds }) => `${days} days, ${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds)}`}
                            />
                        </Typography>
                    )}

                    {cycle.testerStatus === TesterStatusInCycle.Approved && (
                        <Tooltip
                            title={finishEnabled ? '' : 'To finish, report your bugs (New bug) and complete the Test plan, if there is one.'}
                            placement="bottom-end"
                        >
                            {/* span: tooltips need an enabled element to attach to */}
                            <span>
                                <Button
                                    variant="contained"
                                    color="success"
                                    endIcon={<CheckIcon />}
                                    disabled={!finishEnabled}
                                    onClick={() => finishEnabled && dispatch(actions.completeCycle(cycle.id))}
                                >
                                    I have finished
                                </Button>
                            </span>
                        </Tooltip>
                    )}

                    {completed && (
                        <Tooltip title="You have completed the test cycle. For your convenience you can still add and edit bugs.">
                            <Chip icon={<CheckIcon />} label="Completed" color="success" variant="outlined" />
                        </Tooltip>
                    )}
                </Box>

                <Tabs value={activeIndex === -1 ? false : activeIndex} variant="scrollable" scrollButtons="auto" aria-label="Cycle navigation">
                    {tabs.map((tab) => (
                        <Tab
                            key={tab.to}
                            onClick={() => history.push(tab.to)}
                            sx={{ textTransform: 'none', minHeight: 44 }}
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {tab.label}
                                    {tab.count}
                                </Box>
                            }
                        />
                    ))}
                </Tabs>
            </Box>
        </Box>
    );
};

export default TesterCycleNav;
